import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Text, Flex } from "@mantine/core";
import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import { saveIdea } from "../../Api";
import { useKnowledgeData } from "../../hooks/useKnowledgeData";
import { useFields } from "../../hooks/useFields";
import { ideaKeys } from "../../hooks/ideaPage/ideaKeys";
import TagMenu from "./TagMenu";

interface IdeaTagsByCategoryProps {
  idea: any;
  showAddBtn?: boolean;
  isReadOnly?: boolean;
}

interface CategoryRow {
  _id: string | null;
  name: string;
  fieldPath: string;
  isSingleSelect: boolean;
}

// Helper: normalise a raw idea field value to string[]
function readTagsFromIdea(idea: any, fieldPath: string): string[] {
  const raw = idea?.[fieldPath];
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "string") return [raw];
  return [];
}

// ── Single row component with its own local state ───────────────────────────
interface RowProps {
  row: CategoryRow;
  idea: any;
  opts: any[];
  showAddBtn: boolean;
  isReadOnly: boolean;
  auth0: any;
  queryClient: any;
}

const CategoryTagRow: React.FC<RowProps> = ({
  row, idea, opts, showAddBtn, isReadOnly, auth0, queryClient,
}) => {
  const [selected, setSelected] = useState<string[]>(() =>
    readTagsFromIdea(idea, row.fieldPath)
  );
  // Guards against the idea prop changing mid-save and wiping the optimistic update
  const isSavingRef = React.useRef(false);

  // Sync from server — suppressed while a save is in flight
  useEffect(() => {
    if (!isSavingRef.current) {
      setSelected(readTagsFromIdea(idea, row.fieldPath));
    }
  }, [idea, row.fieldPath]);

  const handleToggle = useCallback(
    async (tagId: string) => {
      let updated: string[];
      if (row.isSingleSelect) {
        updated = selected.includes(tagId) ? [] : [tagId];
      } else {
        updated = selected.includes(tagId)
          ? selected.filter((id) => id !== tagId)
          : [...selected, tagId];
      }

      // Optimistic update — show immediately in the UI
      isSavingRef.current = true;
      setSelected(updated);

      // Default tags save without extraFields; category-specific fields use extraFields.
      // Single-select saves as a one-element array (or null) — matches TagsCell behaviour.
      const categoryValue = updated.length > 0 ? updated : null;
      const savePayload = row._id === null
        ? { id: idea._id, tags: updated }
        : { id: idea._id, [row.fieldPath]: categoryValue, extraFields: true };

      try {
        await saveIdea(savePayload, auth0);
        // Patch the cache so the idea prop reflects the new value on next sync
        queryClient.setQueryData(
          ideaKeys.detail(idea._id),
          (old: any) => {
            if (!old) return old;
            if (row._id === null) return { ...old, tags: updated };
            return { ...old, [row.fieldPath]: categoryValue };
          }
        );
      } catch (err) {
        console.error("Error saving tag:", err);
        setSelected(readTagsFromIdea(idea, row.fieldPath));
      } finally {
        isSavingRef.current = false;
      }
    },
    [selected, row, idea, auth0, queryClient]
  );

  return (
    <Flex
      align="stretch"
      sx={{ border: "1px solid #D8D8DB", borderRadius: 6, background: "white" }}
    >
      {/* Category name */}
      <Box
        px={10}
        sx={{
          borderRight: "1px solid #D8D8DB",
          background: "#F8F9FA",
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Text fz={12} fw={600} color="#495057" sx={{ whiteSpace: "nowrap" }}>
          {row.name}
        </Text>
      </Box>

      {/* Tag selector */}
      <Box sx={{ flex: 1, minWidth: 80 }}>
        <TagMenu
          selectedTags={selected}
          onTagToggle={handleToggle}
          tagsOptions={opts}
          colTitle={row.name}
          showAddBtn={showAddBtn && !isReadOnly}
          isReadOnly={isReadOnly}
          singleSelect={row.isSingleSelect}
        />
      </Box>
    </Flex>
  );
};

// ── Container ────────────────────────────────────────────────────────────────
export const IdeaTagsByCategory: React.FC<IdeaTagsByCategoryProps> = ({
  idea,
  showAddBtn = true,
  isReadOnly = false,
}) => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  const { tagCategories, tagsOptions } = useKnowledgeData();
  const { addedAttributes } = useFields();

  const safeCats = Array.isArray(tagCategories) ? tagCategories : [];
  const safeOpts = Array.isArray(tagsOptions) ? tagsOptions : [];
  const safeAttrs = Array.isArray(addedAttributes) ? addedAttributes : [];

  const rows: CategoryRow[] = useMemo(() => {
    const result: CategoryRow[] = [];
    safeAttrs.forEach((attr: any) => {
      if (!attr.fieldPath) return;
      if (!attr.category) {
        if (attr.fieldPath === "tags") {
          result.push({ _id: null, name: "Tags", fieldPath: "tags", isSingleSelect: false });
        }
        return;
      }
      const cat = safeCats.find((c: any) => c._id === attr.category);
      if (!cat) return;
      result.push({
        _id: cat._id,
        name: cat.name,
        fieldPath: attr.fieldPath,
        isSingleSelect: !cat.isMultiSelect,
      });
    });
    return result;
  }, [safeAttrs, safeCats]);

  const getTagsForCategory = useCallback(
    (categoryId: string | null) =>
      categoryId === null
        ? safeOpts.filter((t: any) => !t.categoryId)
        : safeOpts.filter((t: any) => t.categoryId === categoryId),
    [safeOpts]
  );

  const visibleRows = rows.filter(
    (row) => getTagsForCategory(row._id).length > 0
  );

  if (visibleRows.length === 0) return null;

  return (
    <Flex gap={8} wrap="wrap" align="center">
      {visibleRows.map((row) => (
        <CategoryTagRow
          key={row.fieldPath}
          row={row}
          idea={idea}
          opts={getTagsForCategory(row._id)}
          showAddBtn={showAddBtn}
          isReadOnly={isReadOnly}
          auth0={auth0}
          queryClient={queryClient}
        />
      ))}
    </Flex>
  );
};

export default IdeaTagsByCategory;
